import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL } from "@/lib/api";
const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const ImportCSVModal = ({ open, onOpenChange, onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const VALID_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
  const isValidFile = (name) => VALID_EXTENSIONS.some(ext => name.toLowerCase().endsWith(ext));

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (isValidFile(file.name)) {
        setSelectedFile(file);
      } else {
        toast.error("Por favor selecciona un archivo CSV o Excel (.xlsx, .xls)");
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFile(file.name)) {
        setSelectedFile(file);
      } else {
        toast.error("Por favor selecciona un archivo CSV o Excel (.xlsx, .xls)");
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Por favor selecciona un archivo CSV");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API}/import/csv`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(
        <div className="flex items-start space-x-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div>
            <p className="font-semibold">{response.data.message}</p>
            {response.data.errors && response.data.errors.length > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {response.data.errors.length} advertencias durante la importación
              </p>
            )}
          </div>
        </div>
      );

      // Reset state
      setSelectedFile(null);
      onOpenChange(false);
      
      // Callback to refresh data
      if (onImportSuccess) {
        onImportSuccess();
      }

    } catch (error) {
      console.error("Error uploading CSV:", error);
      const errorMessage = error.response?.data?.detail || "Error al subir el archivo";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="import-csv-modal">
        <DialogHeader>
          <DialogTitle className="heading-font text-xl">Actualizar Inventario</DialogTitle>
          <DialogDescription className="body-font">
            Sube un archivo CSV o Excel (.xlsx) para actualizar la base de datos del inventario
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Drag and Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? "border-sky-500 bg-sky-50"
                : "border-border hover:border-sky-400"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="drop-zone"
          >
            {selectedFile ? (
              <div className="space-y-2">
                <FileUp className="w-12 h-12 mx-auto text-[#FF9900]" />
                <p className="body-font text-sm font-medium text-foreground">
                  {selectedFile.name}
                </p>
                <p className="body-font text-xs text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                >
                  Cambiar archivo
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                <p className="body-font text-sm text-muted-foreground">
                  Arrastra un archivo CSV o Excel aquí o
                </p>
                <label htmlFor="csv-file-input">
                  <Button
                    variant="outline"
                    size="sm"
                    className="body-font"
                    onClick={() => document.getElementById('csv-file-input').click()}
                    type="button"
                  >
                    Seleccionar archivo
                  </Button>
                </label>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>
            )}
          </div>

          {/* Info Alert */}
          <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="body-font text-xs text-amber-800">
              <p className="font-semibold mb-1">Importante:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>El archivo debe contener columnas: CODIGO, DESCRIPCION, FAMILIA, SUBFAMILIA, UNIDAD, CANTIDAD_INICIAL, INGRESO, SALIDA, STOCK, PMENOR, PMAYOR, PROVEEDOR, LOTE, COD_BARRA</li>
                <li>Formatos aceptados: CSV (.csv), Excel (.xlsx, .xls)</li>
                <li>Esta acción reemplazará todos los productos existentes</li>
                <li>Se recomienda hacer respaldo antes de importar</li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={uploading}
            data-testid="cancel-import-button"
            className="body-font"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            data-testid="upload-csv-button"
            className="body-font"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Subiendo...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Actualizar Inventario
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCSVModal;
